import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { getCart } from '@/services/Cart/getCart'
import { CSC_APPLE_PAY_GATEWAY } from '@/services/Configuration'
import { getOrder } from '@/services/FramedCheckout/orderServices'
import { error, log } from '@/services/Log'
import type { StandardCustomer } from '@/types/ShopFront/StandardCustomer'
import type { ApplePayHandlerBuilderInput } from '../types'
import { withSessionCancelationHandling } from './withSessionCancelationHandling'

const rawHandlePaymentAuthorized = ({
  session,
  credentials,
  userType,
  customer,
  onAbort,
  beforeFinalize,
}: ApplePayHandlerBuilderInput & {
  customer?: StandardCustomer | null
  userType: 'guest' | 'registered'
}) => async (
  event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
) => {
  log('Apple Pay onpaymentauthorized', event)
  const [
    { updateBillingAddress },
    { updateShippingAddress },
    { completePayment },
    { applePayPaymentContactToStandardAddress },
    { goToConfirmation },
  ] = await allPromisesWithRetries(() => [
    import('@/services/FramedCheckout/paymentServices/updateBillingAddress'),
    import('@/services/FramedCheckout/shipmentServices'),
    import('@/services/FramedCheckout/paymentServices/completePayment'),
    import('@/services/ApplePay/transformers/applePayPaymentContactToStandardAddress'),
    import('@/services/FramedCheckout/navigationServices'),
  ])

  if (!event.payment.billingContact) {
    error('handlePaymentAuthorized billingContact is required', event)
    return session.completePayment({
      status: ApplePaySession.STATUS_FAILURE,
      errors: [{
        code: 'billingContactInvalid',
        message: 'Billing contact is required',
      }],
    })
  }
  const cart = await getCart()
  if (!cart) {
    error('handlePaymentAuthorized No cart found')
    await onAbort()
    throw new Error('No cart found')
  }

  if (!event.payment.shippingContact) {
    return session.completePayment({
      status: ApplePaySession.STATUS_FAILURE,
      errors: [{
        code: 'shippingContactInvalid',
        message: 'Shipping contact is missing',
      }],
    })
  }

  let order = await getOrder({ cart })
  const shippingAddress = applePayPaymentContactToStandardAddress(event.payment.shippingContact)
  const updateShippingResult = await updateShippingAddress({
    address: shippingAddress,
    order,
  })

  if (!updateShippingResult.success || !updateShippingResult.newOrder) {
    return session.completePayment({
      status: ApplePaySession.STATUS_FAILURE,
      errors: [{
        code: 'shippingContactInvalid',
        message: 'Shipping contact is invalid',
      }],
    })
  }

  order = updateShippingResult.newOrder

  const billingAddress = applePayPaymentContactToStandardAddress(event.payment.billingContact)
  billingAddress.email = billingAddress.email || shippingAddress.email
  billingAddress.phone = billingAddress.phone || shippingAddress.phone
  if (!billingAddress.email || !billingAddress.phone) {
    return session.completePayment({
      status: ApplePaySession.STATUS_FAILURE,
      errors: [{
        code: 'billingContactInvalid',
        message: 'Billing contact is missing email or phone',
      }],
    })
  }
  const updateBillingResult = await updateBillingAddress({
    order,
    billingAddress,
    customer,
  })
  if (!updateBillingResult.success || !updateBillingResult.newOrder) {
    error('handlePaymentAuthorized updateBillingAddress failed', updateBillingResult)
    return session.completePayment({
      status: ApplePaySession.STATUS_FAILURE,
      errors: [{
        code: 'billingContactInvalid',
        message: 'Billing contact is invalid',
      }],
    })
  }
  try {
    const { success, orderId } = await completePayment({
      paymentsHost: String(CSC_APPLE_PAY_GATEWAY),
      applePayPaymentData: btoa(JSON.stringify(event.payment.token.paymentData)),
      userType,
      order: updateBillingResult.newOrder,
      credentials,
      paymentIntentData: {
        walletName: 'applePay',
      },
    })
    log('completePayment result', { success, orderId })
    if (success && orderId) {
      session.completePayment({
        'status': ApplePaySession.STATUS_SUCCESS,
      })
      if (beforeFinalize) {
        try {
          await beforeFinalize()
        } catch (err) {
          error('handlePaymentAuthorized beforeFinalize failed', error)
        }
      }
      log(`goToConfirmation orderId:${orderId}, billingAddress.zip:${updateBillingResult.newOrder.billingAddress.zip}`)
      await goToConfirmation(orderId, updateBillingResult.newOrder.billingAddress.zip)
      return orderId
    }
  } catch (err) {
    error('Payment Failed', err)
  }
  return session.completePayment({
    'status': ApplePaySession.STATUS_FAILURE,
    'errors': [{
      'code': 'unknown',
      'message': 'Payment failed',
    }],
  })
}

export const handlePaymentAuthorized = withSessionCancelationHandling(rawHandlePaymentAuthorized)
export default handlePaymentAuthorized
